import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useUpdateGameV2 } from "@seabrookstudios/pitch2table";
import { FullScreenDialog } from "../core/FullScreenDialog";
import Box from "@mui/material/Box";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import CardHeader from "@mui/material/CardHeader";
import { formatAge } from "@seabrookstudios/pitch2table-core";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").Game} props.game
 */
export const EditAgeControl = ({ game }) => {
  const onUpdateSuccess = () => {
    enqueueSnackbar("Saved", { variant: "success" });
  };

  const onUpdateFailure = () => {
    enqueueSnackbar("Update failed", { variant: "error" });
  };

  const updateGame = useUpdateGameV2(game.id, onUpdateSuccess, onUpdateFailure);

  /**
   * @param {number} age
   * @param {import('@seabrookstudios/pitch2table-core').SupportPlayerCounts} playerCounts
   */
  const onSave = async (age, playerCounts) => {
    await updateGame.mutateAsync({
      age,
      playerCounts,
      sizzle: undefined,
      bgg: undefined,
      personalGoal: undefined,
    });
  };

  const [value, setValue] = useState(game.age || "");
  const [disabled, setDisabled] = useState(true);

  const onSaveHandler = () => {
    onSave(value);
  };

  useEffect(() => {
    setDisabled(!value || value.length === 0);
  }, [value]);

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const setAgeFromEvent = (e) => {
    const v = e.target.value;

    setAge(v);
  };

  /**
   * @param {string|number|undefined} n
   */
  const setAge = (n) => {
    if (!n) {
      setValue("");
      return;
    }

    setValue(`${n}`);
  };

  /**
   * @param {number|undefined} n
   */
  const setQuick = (n) => {
    const onPressHandler = () => {
      setAge(`${n}`);
    };

    return onPressHandler;
  };

  return (
    <FullScreenDialog
      title={`Edit Age`}
      actionText="SAVE"
      onSaveHandler={onSaveHandler}
      isBusy={updateGame.isPending}
      CustomControl={<ListItemText primary={"Age"} secondary={formatAge(game.age) || "Click to edit."} />}
      canSave={disabled}
    >
      <Box p={1}>
        <Stack direction="row">
          <TextField
            style={{ width: "100%" }}
            value={value}
            label="Age"
            autoFocus
            onChange={setAgeFromEvent}
            type="number"
            helperText="Minimum age for players to enjoy the game."
          />
        </Stack>
      </Box>
      <Box padding={1}>
        <CardHeader subheader="Quick Options" />
        <Grid container spacing={2}>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(3)}>
              3+
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(4)}>
              4+
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(5)}>
              5+
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(6)}>
              6+
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(8)}>
              8+
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(10)}>
              10+
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(12)}>
              12+
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(14)}>
              14+
            </Button>
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <Button style={{ width: "100%" }} variant="outlined" onClick={setQuick(18)}>
              18+
            </Button>
          </Grid>
        </Grid>
      </Box>
    </FullScreenDialog>
  );
};
